import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { domain_selection_list, getCloudOrgScoreService } from '../../services';
import ScreenLoader from '../../Components/ScreenLoader';
import '../../styles/AddOns.scss';

function BankUserDomains() {
    const { frameworkId } = useParams();
    const [domains, setDomains] = useState([]);
    const [frameworkName, setFrameworkName] = useState('');
    const [frameworkDetail, setFrameworkDetail] = useState({});
    const [loading, setLoading] = useState(true);

    // Cloud Org Score states
    const [cloudScoreData, setCloudScoreData] = useState(null);
    const [cloudScoreLoading, setCloudScoreLoading] = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchDomains = async () => {
            try {
                setLoading(true);
                const response = await domain_selection_list(frameworkId);
                console.log(response, 'asdadasd');
                const data = response?.statusCode?.response_data?.data?.domains || [];
                const frameworkDetail = response?.statusCode?.response_data?.data?.framework || {};
                setDomains(Array.isArray(data) ? data : []);
                setFrameworkDetail(frameworkDetail);
                // Get framework name from localStorage or first domain response
                const name = localStorage.getItem('selectedFrameworkName') || 'Framework';
                setFrameworkName(name);
            } catch (error) {
                console.error('Error fetching domains:', error);
            } finally {
                setLoading(false);
            }
        };
        const fetchCloudScore = async () => {
            try {
                setCloudScoreLoading(true);
                const response = await getCloudOrgScoreService();
                // Ensure we get the nested data from response_data.data as per user snippet
                const scoreData = response?.statusCode?.response_data?.data || null;
                setCloudScoreData(scoreData);
            } catch (error) {
                console.error("Error fetching cloud org score:", error);
            } finally {
                setCloudScoreLoading(false);
            }
        };

        if (frameworkId) {
            fetchDomains();
            fetchCloudScore();
        }
    }, [frameworkId]);

    const getScoreColor = (percent) => {
        if (percent >= 70) return '#10B981';
        if (percent >= 40) return '#F97316';
        return '#EF4444';
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <ScreenLoader />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            {/* Header */}
            <div className="d-flex align-items-center gap-3 mb-4">
                <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => navigate(-1)}
                    style={{ color: '#94A3B8', borderColor: '#334155' }}
                >
                    ← Back
                </button>
                <h3 className="color_white mb-0 font_20 font-600">{frameworkName} — Domains</h3>
            </div>

            {/* Domains Grid */}
            {domains.length === 0 ? (
                <div className="text-center py-5">
                    <p className="text-white" style={{ fontSize: '16px' }}>No domains found for this framework.</p>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '16px',
                }}>
                    {domains.map((domain, idx) => {
                        const score = domain?.compliance_percentage || domain?.score || 0;
                        const totalControls = domain?.total_controls || domain?.total_questions || 0;
                        const passingControls = domain?.passing_controls || domain?.passing_requirements || 0;
                        const scoreColor = getScoreColor(score);

                        const getDomainImage = (name) => {
                            const lowerName = (name || '').toLowerCase();
                            if (lowerName.includes('aws') || lowerName.includes('amazon')) return 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/aws.svg';
                            if (lowerName.includes('azure') || lowerName.includes('microsoft')) return 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/microsoft-azure.svg';
                            if (lowerName.includes('gcp') || lowerName.includes('google')) return 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/googlecloud.svg';
                            return null;
                        };
                        const domainImg = getDomainImage(domain?.name);

                        return (
                            <div
                                key={domain?.compliance_domain_id || idx}
                                className="bank-domain-card"
                                onClick={() => {
                                    const domId = domain?.compliance_domain_id || domain?.id;
                                    localStorage.setItem('selectedDomainName', domain?.name || '');
                                    navigate(`/bank-user/framework/${frameworkId}/domain/${domId}/controls`);
                                }}
                            >
                                <div className="bdc-logo">
                                    {domainImg ? (
                                        <img src={domainImg} alt={domain?.name} />
                                    ) : (
                                        <span className="bdc-logo-fallback">{domain?.name?.substring(0, 3)?.toUpperCase() || 'DOM'}</span>
                                    )}
                                </div>
                                <div className="bdc-content">
                                    <h4 className="bdc-title">{domain?.name}</h4>

                                    <div className="bdc-score-row">
                                        <span className="bdc-score-label">Score:</span>
                                        <div className="bdc-progress-track">
                                            <div
                                                className="bdc-progress-bar"
                                                style={{ width: `${score}%`, backgroundColor: scoreColor }}
                                            />
                                        </div>
                                        <span className="bdc-score-value">{score}%</span>
                                    </div>

                                    <div className="bdc-bottom-row">
                                        <span className="bdc-stats">{passingControls} / {totalControls} Passing Requirements</span>
                                        <button
                                            className="bdc-download-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                console.log("Download clicked");
                                            }}
                                            title="Download Report"
                                        >
                                            <svg viewBox="0 0 24 24">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="7 10 12 15 17 10" />
                                                <line x1="12" y1="15" x2="12" y2="3" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Prowler Domains Card — shown only when add_prowler_domains & bank_audit_valid are true */}
                    {frameworkDetail?.add_prowler_domains === true &&
                        JSON.parse(localStorage.getItem('user_profile') || '{}')?.bank_audit_valid === true && (
                            <div
                                className="bank-domain-card"
                                onClick={() => {
                                    navigate(`/integration-cloud`);
                                }}
                            >
                                <div className="bdc-logo">
                                    <span className="bdc-logo-fallback">CC</span>
                                </div>
                                <div className="bdc-content">
                                    <h4 className="bdc-title">Cloud Configuration</h4>

                                    {cloudScoreLoading ? (
                                        <div className="bdc-score-row mt-2">
                                            <span style={{ fontSize: '12px', color: '#94A3B8' }}>Loading score...</span>
                                        </div>
                                    ) : cloudScoreData ? (() => {
                                        const total = cloudScoreData.total_cases || 0;
                                        const pass = cloudScoreData.total_pass || 0;
                                        const score = total > 0 ? Math.round((pass / total) * 100) : 0;

                                        return (
                                            <>
                                                <div className="bdc-score-row mt-2">
                                                    <span className="bdc-score-label">Score:</span>
                                                    <div className="bdc-progress-track">
                                                        <div
                                                            className="bdc-progress-bar"
                                                            style={{
                                                                width: `${score}%`,
                                                                backgroundColor: getScoreColor(score)
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="bdc-score-value">{score}%</span>
                                                </div>

                                                <div className="bdc-bottom-row mt-2">
                                                    <span className="bdc-stats">
                                                        {pass} / {total} Passing Requirements
                                                    </span>
                                                </div>
                                            </>
                                        );
                                    })() : (
                                        <>
                                            <div className="bdc-score-row">
                                                <span className="bdc-score-label">Cloud Security Audit</span>
                                            </div>

                                            <div className="bdc-bottom-row">
                                                <span className="bdc-stats">View prowler scan results</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                </div>
            )}
        </div>
    );
}

export default BankUserDomains;
